import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/i18n/I18nContext";
import { useToast } from "@/hooks/use-toast";
import { TRY_FREE } from "@/constants/testIds";
import { PromoBadge } from "@/components/landing/PromoBadge";

const QUICK_STYLES = ["pop", "rap", "rock", "electro", "jazz", "latino"];
const QUICK_MOODS = ["joyeux", "romantique", "motivant", "triste"];

/**
 * Landing page hook: instead of generating an actual free AI preview here (removed - it forced
 * visitors to wait 1-5 minutes on the homepage before even reaching the real wizard), this now
 * just captures the idea/style/mood and jumps straight into the real "Create my song" wizard
 * (step 1) with those choices pre-filled, via the same `ams_wizard_draft` localStorage key the
 * wizard already reads on mount (Itération 56, 12/08/2026).
 */
export default function TryForFreeBlock() {
  const { t } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("pop");
  const [mood, setMood] = useState("joyeux");

  const handleContinue = () => {
    if (prompt.trim().length < 3) {
      toast({ variant: "destructive", description: t("tryFree.promptTooShort") });
      return;
    }
    localStorage.setItem("ams_wizard_draft", JSON.stringify({ subject: prompt.trim(), style, mood }));
    localStorage.setItem("ams_wizard_step", "0");
    navigate("/create");
  };

  return (
    <div
      data-testid={TRY_FREE.container}
      className="relative mx-auto mt-12 max-w-2xl overflow-hidden rounded-3xl border border-primary/25 bg-card/60 p-6 shadow-2xl shadow-primary/10 backdrop-blur-xl sm:p-8"
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />

      <div className="relative flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-primary">
        <Sparkles className="h-3.5 w-3.5" />
        {t("tryFree.eyebrow")}
      </div>
      <h2 className="relative mt-3 text-center font-heading text-2xl font-medium sm:text-3xl">{t("tryFree.title")}</h2>
      <p className="relative mt-2 text-center text-sm text-muted-foreground">{t("tryFree.subtitle")}</p>
      <div className="relative text-center"><PromoBadge variant="card" /></div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative mt-6 space-y-4">
        <Textarea
          data-testid={TRY_FREE.promptInput}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t("tryFree.placeholder")}
          maxLength={300}
          className="min-h-24 rounded-2xl border-border/60 bg-background/60 text-base"
        />
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("tryFree.styleLabel")}</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_STYLES.map((s) => (
              <button
                key={s}
                type="button"
                data-testid={TRY_FREE.styleChip(s)}
                onClick={() => setStyle(s)}
                className={`rounded-full border px-3.5 py-1.5 text-sm font-medium capitalize transition-all ${
                  style === s ? "border-primary bg-primary text-primary-foreground" : "border-border/60 text-muted-foreground hover:border-primary/40"
                }`}
              >
                {t(`options.style_${s}`)}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("tryFree.moodLabel")}</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_MOODS.map((m) => (
              <button
                key={m}
                type="button"
                data-testid={TRY_FREE.moodChip(m)}
                onClick={() => setMood(m)}
                className={`rounded-full border px-3.5 py-1.5 text-sm font-medium capitalize transition-all ${
                  mood === m ? "border-primary bg-primary text-primary-foreground" : "border-border/60 text-muted-foreground hover:border-primary/40"
                }`}
              >
                {t(`options.mood_${m}`)}
              </button>
            ))}
          </div>
        </div>
        <Button
          onClick={handleContinue}
          size="lg"
          data-testid={TRY_FREE.generateBtn}
          className="group h-13 w-full rounded-full text-base shadow-lg shadow-primary/30 transition-transform hover:-translate-y-0.5"
        >
          <Sparkles className="mr-2 h-4 w-4" />
          {t("tryFree.generateBtn")}
        </Button>
      </motion.div>
    </div>
  );
}
